// Copyright 2026, Auxilio Technologies LLC

/**
 * Role-Based Access Control (RBAC) for Optibloom.
 *
 * Roles (must match backend app/core/security.py Roles):
 * - superuser: full system access (Auxilio)
 * - admin:     shop owner — full operational access + user management (sees everything)
 * - manager:   order board, add order, route builder, dispatch, notifications
 * - florist:   prep queue, mark ready
 * - driver:    driver view, record delivery outcomes
 * - viewer:    read-only access
 */

export const Roles = {
  SUPERUSER: 'superuser',
  ADMIN: 'admin',
  MANAGER: 'manager',
  FLORIST: 'florist',
  DRIVER: 'driver',
  VIEWER: 'viewer',
} as const;

export type RoleType = typeof Roles[keyof typeof Roles];

export const Permissions = {
  DASHBOARD_READ: 'dashboard:read',
  ORDERS_READ: 'orders:read',
  ORDERS_WRITE: 'orders:write',
  ORDERS_DELETE: 'orders:delete',
  PREP_READ: 'prep:read',
  PREP_WRITE: 'prep:write',
  DISPATCH_READ: 'dispatch:read',
  DISPATCH_WRITE: 'dispatch:write',
  DRIVER_READ: 'driver:read',
  DRIVER_WRITE: 'driver:write',
  NOTIFICATIONS_READ: 'notifications:read',
  NOTIFICATIONS_WRITE: 'notifications:write',
  SETTINGS_READ: 'settings:read',
  SETTINGS_WRITE: 'settings:write',
  USERS_READ: 'users:read',
  USERS_WRITE: 'users:write',
  USERS_INVITE: 'users:invite',
  SYSTEM_CONFIG: 'system:config',
} as const;

export type PermissionType = typeof Permissions[keyof typeof Permissions];

const ALL_PERMISSIONS = Object.values(Permissions);

export const RolePermissions: Record<RoleType, PermissionType[]> = {
  [Roles.SUPERUSER]: [...ALL_PERMISSIONS],

  [Roles.ADMIN]: [
    Permissions.DASHBOARD_READ,
    Permissions.ORDERS_READ, Permissions.ORDERS_WRITE, Permissions.ORDERS_DELETE,
    Permissions.PREP_READ, Permissions.PREP_WRITE,
    Permissions.DISPATCH_READ, Permissions.DISPATCH_WRITE,
    Permissions.DRIVER_READ, Permissions.DRIVER_WRITE,
    Permissions.NOTIFICATIONS_READ, Permissions.NOTIFICATIONS_WRITE,
    Permissions.SETTINGS_READ, Permissions.SETTINGS_WRITE,
    Permissions.USERS_READ, Permissions.USERS_WRITE, Permissions.USERS_INVITE,
  ],

  [Roles.MANAGER]: [
    Permissions.DASHBOARD_READ,
    Permissions.ORDERS_READ, Permissions.ORDERS_WRITE, Permissions.ORDERS_DELETE,
    Permissions.PREP_READ,
    Permissions.DISPATCH_READ, Permissions.DISPATCH_WRITE,
    Permissions.DRIVER_READ,
    Permissions.NOTIFICATIONS_READ, Permissions.NOTIFICATIONS_WRITE,
    Permissions.SETTINGS_READ,
  ],

  [Roles.FLORIST]: [
    Permissions.DASHBOARD_READ,
    Permissions.ORDERS_READ,
    Permissions.PREP_READ, Permissions.PREP_WRITE,
    Permissions.SETTINGS_READ,
  ],

  [Roles.DRIVER]: [
    Permissions.DASHBOARD_READ,
    Permissions.DRIVER_READ, Permissions.DRIVER_WRITE,
    Permissions.SETTINGS_READ,
  ],

  [Roles.VIEWER]: [
    Permissions.DASHBOARD_READ,
    Permissions.ORDERS_READ,
    Permissions.PREP_READ,
    Permissions.DISPATCH_READ,
    Permissions.DRIVER_READ,
    Permissions.SETTINGS_READ,
  ],
};

export interface RoleInfo {
  id: RoleType;
  nameKey: string;       // i18n key under roles.*
  level: number;         // higher = more privileges
  color: string;         // tailwind color stem
}

export const RoleInfoMap: Record<RoleType, RoleInfo> = {
  [Roles.SUPERUSER]: { id: Roles.SUPERUSER, nameKey: 'roles.superuser', level: 100, color: 'purple' },
  [Roles.ADMIN]: { id: Roles.ADMIN, nameKey: 'roles.admin', level: 90, color: 'rose' },
  [Roles.MANAGER]: { id: Roles.MANAGER, nameKey: 'roles.manager', level: 70, color: 'blue' },
  [Roles.FLORIST]: { id: Roles.FLORIST, nameKey: 'roles.florist', level: 50, color: 'emerald' },
  [Roles.DRIVER]: { id: Roles.DRIVER, nameKey: 'roles.driver', level: 40, color: 'amber' },
  [Roles.VIEWER]: { id: Roles.VIEWER, nameKey: 'roles.viewer', level: 10, color: 'gray' },
};

export function hasPermission(userRole: RoleType, permission: PermissionType): boolean {
  const permissions = RolePermissions[userRole];
  if (!permissions) return false;
  return permissions.includes(permission);
}

export function getRoleInfo(role: RoleType): RoleInfo {
  return RoleInfoMap[role] || RoleInfoMap[Roles.VIEWER];
}

export function isAdminRole(role: RoleType): boolean {
  return role === Roles.SUPERUSER || role === Roles.ADMIN;
}

export function canManageUsers(role: RoleType): boolean {
  return hasPermission(role, Permissions.USERS_READ);
}

// Roles a user can assign (strictly lower level than their own)
export function getAssignableRoles(userRole: RoleType): RoleType[] {
  const userLevel = RoleInfoMap[userRole]?.level || 0;
  return Object.values(Roles).filter((role) => (RoleInfoMap[role]?.level || 0) < userLevel);
}
